import {
  Component,
  computed,
  contentChild,
  contentChildren,
  input,
  model,
  output,
  AfterContentInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { StepComponent } from '../step/step.component';
import { NgTemplateOutlet } from '@angular/common';
import { StepFooterComponent } from '../step-footer/step-footer.component';
import { Thumbnail } from './stepper.model';
import { MibButtonComponent, MibSpinnerComponent } from '../../components';

@Component({
  selector: 'mib-stepper',
  imports: [NgTemplateOutlet, MibButtonComponent, MibSpinnerComponent],
  templateUrl: './stepper.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './stepper.component.css',
})
export class StepperComponent implements AfterContentInit {
  protected readonly steps = contentChildren<StepComponent>(StepComponent);
  protected readonly footer = contentChild<StepFooterComponent>(StepFooterComponent);

  readonly currentStepIndex = model<number>(0);
  protected readonly activeStep = computed(() => this.steps()[this.currentStepIndex()]);

  readonly displayNext = input(true);
  readonly thumbnails = input<Thumbnail[]>();
  readonly displayPrevious = input(true);
  readonly stepChange = output<number>();

  readonly canStepGoPrevious = computed(
    () => this.steps()[this.currentStepIndex()].canGoPrevious() && !!this.previousStepActive(),
  );
  readonly canStepGoNext = computed(() => this.steps()[this.currentStepIndex()].canGoNext() && !!this.nextStepActive());

  ngAfterContentInit(): void {
    if (this.steps()[this.currentStepIndex()].disabled()) {
      this.nextStepActive();
    }
  }

  goToStep(i: number): void {
    if (i >= 0 && i < this.steps().length && this.steps()[i].disabled()) {
      this.currentStepIndex.set(i);
    }
  }

  doNext(): void {
    const nextStep = this.nextStepActive();
    if (nextStep) {
      this.currentStepIndex.set(this.steps().indexOf(nextStep));
      this.stepChange.emit(this.currentStepIndex());
    }
  }

  doPrevious(): void {
    const previousStep = this.previousStepActive();
    if (previousStep) {
      this.currentStepIndex.set(this.steps().indexOf(previousStep));
      this.stepChange.emit(this.currentStepIndex());
    }
  }

  protected hasNext(): boolean {
    return !!this.nextStepActive();
  }

  protected hasPrevious(): boolean {
    return !!this.previousStepActive();
  }

  private nextStepActive(): StepComponent | undefined {
    const stepsArray = this.steps();
    for (let i = this.currentStepIndex() + 1; i < stepsArray.length; i++) {
      if (!stepsArray[i].disabled()) {
        return stepsArray[i];
      }
    }
    return undefined;
  }

  private previousStepActive(): StepComponent | undefined {
    const stepsArray = this.steps();
    for (let i = this.currentStepIndex() - 1; i >= 0; i--) {
      if (!stepsArray[i].disabled()) {
        return stepsArray[i];
      }
    }
    return undefined;
  }
}
